import React, { useCallback } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { MessageSquare, Users } from 'lucide-react';
import type { Conversation } from '../../../types/messages';
import { cn } from '../../../lib/utils';

interface ConversationItemProps {
  conversation: Conversation;
  onConversationClick?: (conversationId: string) => void;
  currentUserName?: string | null;
}

export function ConversationItem({ conversation, onConversationClick, currentUserName }: ConversationItemProps) {
  const isGroup = conversation.participants.length > 2;
  const hasUnread = conversation.unread_count > 0;

  const handleClick = useCallback(() => {
    if (onConversationClick) {
      onConversationClick(conversation.conversation_id);
    }
  }, [conversation.conversation_id, onConversationClick]);

  // Filter out current user from participant names
  const otherNames = currentUserName
    ? (conversation.participant_names || []).filter((name: string) => name !== currentUserName)
    : (conversation.participant_names || []);

  const displayName = isGroup
    ? otherNames.length > 0
      ? `${otherNames.slice(0, 3).join(', ')}${otherNames.length > 3 ? ` +${otherNames.length - 3}` : ''}`
      : 'Unknown User'
    : otherNames.length > 0
    ? otherNames[0]
    : 'Unknown User';

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-4 text-sm transition-colors hover:bg-muted/50 cursor-pointer',
        hasUnread && 'bg-accent/10'
      )}
      onClick={handleClick}
    >
      <div className="flex-shrink-0 mt-1">
        {isGroup ? (
          <Users className="h-4 w-4 text-muted-foreground" />
        ) : (
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 space-y-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={cn('font-medium truncate', hasUnread && 'font-semibold')}>
            {displayName}
          </p>
          {hasUnread && (
            <span className="inline-flex h-2 w-2 rounded-full bg-primary flex-shrink-0" />
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {conversation.last_message.message}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatDistanceToNowStrict(new Date(conversation.last_message_at), { addSuffix: true })}
        </p>
      </div>
    </div>
  );
}

